import { connect } from 'react-redux';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-final-form';

import { getKnmFormRef, getKnmWatchOnlyByTestUser } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';

import { AgreementsResultDecision } from './components/AgreementsResultDecision/AgreementsResultDecision';
import { AgreementsRequirements } from './components/AgreementsRequirements/AgreementsRequirements';
import { AgreementsPetitionOrganization } from './components/AgreementsPetitionOrganization/AgreementsPetitionOrganization';
import { AgreementsApprovalOfAgreement } from './components/AgreementsApprovalOfAgreement/AgreementsApprovalOfAgreement';
import { AgreementsEliminateViolations } from './components/AgreementsEliminateViolations/AgreementsEliminateViolations';
import { AgreementsCompletionDate } from './components/AgreementsCompletionDate/AgreementsCompletionDate';
import { AgreementsSupportingDocuments } from './components/AgreementsSupportingDocuments/AgreementsSupportingDocuments';
import { IGeneralResultDecision } from './interfaces/IGeneralResultDecision';
import { AgreementsApproveInfo } from './components/AgreementsApproveInfo/AgreementsApproveInfo';
import { AgreementHeaderStatus } from './components/AgreementHeaderStatus/AgreementHeaderStatus';
import { AgreementsTerminated } from './components/AgreementsTerminated/AgreementsTerminated';
import { AgreementsChanges } from './components/AgreementsChanges/AgreementsChanges';
import { AgreementsExecution } from './components/AgreementsExecution/AgreementsExecution';
import { AgreementsContext } from './AgreementsContext';
import { checkHasErrorInCollapseBlock } from '../../functions/checkHasErrorInCollapseBlock';
import { AgreementsProsecutorOrganization } from './components/AgreementsProsecutorOrganization/AgreementsProsecutorOrganization';
import { AgreementsSignInfo } from './components/AgreementsSignInfo/AgreementsSignInfo';
import { AgreementsButtons } from './components/AgreementsButtons/AgreementsButtons';
import { getIsDirtyAgreement } from './components/AgreementsButtons/Buttons/SignButton/getIsDirtyAgreement';

interface IErknmAgreementsItemProps {
  id: string;
  name: string;
  onRemove: () => void;
  resultDecisions: IGeneralResultDecision[] | undefined;
  agreements: IErknmFormAgreement[];
  agreement: IErknmFormAgreement;
  agreementIndex: number;
  editedAgreementGuid: string;
  setEditedAgreementGuid: (val: string) => void;
}

interface IErknmAgreementsItemStoreProps {
  isSupervisor: boolean;
  isProsecutorOrSuperProsecutor: boolean;
  isWatchOnlyByTestUser: boolean;
  formRef: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
}

const ErknmAgreementsItemWithoutStore = ({
  id,
  name,
  onRemove,
  agreements,
  resultDecisions,
  agreement,
  agreementIndex,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  isWatchOnlyByTestUser,
  formRef,
  decisionFormRef,
  editedAgreementGuid,
  setEditedAgreementGuid,
}: IErknmAgreementsItemProps & IErknmAgreementsItemStoreProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const { dirtyFields: currentDirtyFields } = useFormState({ subscription: { dirtyFields: true } });
  const { isDecision } = useContext(AgreementsContext);
  const hasDirtyAgreement = getIsDirtyAgreement(
    currentDirtyFields,
    agreementIndex,
    isDecision ? formRef : decisionFormRef,
  );

  /**
   * Смотрим на изменение полей и если это произошло, то
   * сообщаем родителю, что мы поменялись, заблокируй остальные
   */
  useEffect(() => {
    setEditedAgreementGuid(hasDirtyAgreement.wasEditFiled ? agreement.guid || '' : '');
  }, [hasDirtyAgreement]);

  /**
   * Определение блокировки по редактированию в других соглашениях
   */
  const isWatchOnlyEditOtherAgreements = useMemo(() => {
    const newAgreement = agreements.find(i => i.isNew);

    return (!!editedAgreementGuid && editedAgreementGuid !== agreement.guid) || (!!newAgreement && !agreement.isNew);
  }, [editedAgreementGuid, agreement, agreements]);

  /**
   * Определения блокировки всей формы
   */
  const isWatchOnlyMain = useMemo(() => {
    return (
      isWatchOnlyByTestUser ||
      agreement.status === AgreementStatusEnum.COMPLETED ||
      agreement.status === AgreementStatusEnum.NOT_COMPLETED
    );
  }, [isWatchOnlyByTestUser, agreement]);

  /**
   * Определения блокировки основной формы
   */
  const isWatchOnlyBasicForm = useMemo(() => {
    if (isWatchOnlyMain) {
      return true;
    }

    if (isSupervisor) {
      return (
        isWatchOnlyEditOtherAgreements ||
        (agreement.status !== AgreementStatusEnum.DRAFT && agreement.status !== AgreementStatusEnum.READY_TO_APPROVE)
      );
    }

    return true;
  }, [isSupervisor, isWatchOnlyEditOtherAgreements, editedAgreementGuid, agreement, isWatchOnlyMain]);

  /**
   * Определения блокировки Блока согласования
   */
  const isWatchOnlyApproveForm = useMemo(() => {
    if (isWatchOnlyMain) {
      return true;
    }

    if (isProsecutorOrSuperProsecutor) {
      return isWatchOnlyEditOtherAgreements || agreement.status !== AgreementStatusEnum.ON_APPROVAL;
    }

    return true;
  }, [isProsecutorOrSuperProsecutor, isWatchOnlyMain, editedAgreementGuid, isWatchOnlyEditOtherAgreements, agreement]);

  /**
   * Определения блокировки Блока расторжения
   */
  const isWatchOnlyTerminateForm = useMemo(() => {
    if (isWatchOnlyMain) {
      return true;
    }

    return (
      agreement.status === AgreementStatusEnum.PENDING_TERMINATION ||
      agreement.status === AgreementStatusEnum.TERMINATED ||
      (isProsecutorOrSuperProsecutor && !agreement.signed)
    );
  }, [isWatchOnlyMain, agreement, isProsecutorOrSuperProsecutor]);

  /**
   * Определения блокировки Блока исполнения
   */
  const isWatchOnlyExecutionForm = useMemo(() => {
    if (isWatchOnlyMain) {
      return true;
    }

    return agreement.status === AgreementStatusEnum.NOT_COMPLETED;
  }, [isWatchOnlyMain, agreement]);

  /**
   * Определение необходимости показа раздела о согласовании, исходя из роли пользователя
   */
  const canShowApprovalBlock = useMemo(() => {
    if (isProsecutorOrSuperProsecutor) {
      return (
        agreement.status !== AgreementStatusEnum.DRAFT &&
        agreement.status !== AgreementStatusEnum.CANCELED &&
        agreement.status !== AgreementStatusEnum.READY_TO_APPROVE
      );
    }

    return (
      agreement.status !== AgreementStatusEnum.DRAFT &&
      agreement.status !== AgreementStatusEnum.CANCELED &&
      agreement.status !== AgreementStatusEnum.READY_TO_APPROVE &&
      agreement.status !== AgreementStatusEnum.ON_APPROVAL
    );
  }, [isProsecutorOrSuperProsecutor, isSupervisor]);

  /**
   * Все ли нарушения исполнены
   */
  const areAllViolationCompleted = useMemo(() => {
    let hasUncompleted = false;

    agreement.violations?.forEach(requirement => {
      requirement.violatedMandatory.forEach(mandatory => {
        mandatory.violations?.forEach(violation => {
          if (!hasUncompleted && violation.status !== ViolationStatusesEnum.FIXED) {
            hasUncompleted = true;
          }
        });
      });
    });

    return !hasUncompleted;
  }, [agreement]);

  const hasError = checkHasErrorInCollapseBlock(formRef, name);

  return (
    <KnmCollapse
      theme="fields"
      title={`Соглашение ${agreementIndex + 1}`}
      id={id}
      opened={
        agreement?.status !== AgreementStatusEnum.CANCELED &&
        agreement?.status !== AgreementStatusEnum.DENIED &&
        agreement?.status !== AgreementStatusEnum.TERMINATED &&
        agreement?.status !== AgreementStatusEnum.NOT_COMPLETED
      }
      onClose={isWatchOnlyBasicForm ? undefined : onRemove}
      headerPostfix={<AgreementHeaderStatus agreement={agreement} />}
      changeOpenedCallback={setIsKnmCollapseOpened}
      error={isKnmCollapseOpened && hasError}
    >
      <>
        <AgreementsResultDecision
          resultDecisions={resultDecisions}
          agreements={agreements}
          watchOnly={isWatchOnlyBasicForm}
          name={name}
          id={id}
        />
        <AgreementsRequirements
          resultDecisions={resultDecisions}
          watchOnly={isWatchOnlyBasicForm}
          name={name}
          id={id}
          agreement={agreement}
          agreementIndex={agreementIndex}
        />
        <AgreementsPetitionOrganization
          watchOnly={isWatchOnlyBasicForm}
          name={name}
          id={id}
          agreement={agreement}
          resultDecisions={resultDecisions}
        />
        <AgreementsApprovalOfAgreement isWatchOnly={isWatchOnlyBasicForm} name={name} id={id} agreement={agreement} />
        {agreement.approvalOfAgreement?.value && (
          <>
            <AgreementsEliminateViolations
              isWatchOnly={isWatchOnlyBasicForm}
              name={name}
              id={id}
              agreement={agreement}
              resultDecisions={resultDecisions}
            />
            <AgreementsCompletionDate isWatchOnly={isWatchOnlyBasicForm} name={name} id={id} />
            <AgreementsSupportingDocuments
              isWatchOnly={isWatchOnlyBasicForm}
              name={name}
              id={id}
              agreement={agreement}
            />
            {!canShowApprovalBlock && (
              <AgreementsProsecutorOrganization isWatchOnly={isWatchOnlyBasicForm} name={name} id={id} />
            )}
          </>
        )}
        {canShowApprovalBlock && (
          <AgreementsApproveInfo name={name} id={id} agreement={agreement} isWatchOnly={isWatchOnlyApproveForm} />
        )}
        <AgreementsSignInfo isWatchOnly={isWatchOnlyBasicForm} name={name} id={id} />
        {agreement.changes && agreement.changes.length > 0 && (
          <AgreementsChanges name={name} id={id} isWatchOnlyMain={isWatchOnlyMain} />
        )}
        {agreement.terminate && (
          <AgreementsTerminated name={name} id={id} isWatchOnly={isWatchOnlyTerminateForm} agreement={agreement} />
        )}
        {agreement.execution && (
          <AgreementsExecution
            name={name}
            id={id}
            isWatchOnly={isWatchOnlyExecutionForm}
            agreement={agreement}
            areAllViolationCompleted={areAllViolationCompleted}
          />
        )}
        {!isWatchOnlyEditOtherAgreements && (
          <AgreementsButtons
            name={name}
            agreement={agreement}
            agreementIndex={agreementIndex}
            watchOnlyMain={isWatchOnlyMain}
            areAllViolationCompleted={areAllViolationCompleted}
          />
        )}
      </>
    </KnmCollapse>
  );
};

const mapStateToProps = (state: IRootState): IErknmAgreementsItemStoreProps => ({
  formRef: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
});

export const ErknmAgreementsItem = connect(mapStateToProps)(ErknmAgreementsItemWithoutStore);
